/**
 * Client-side score text parsing shared by ranking/standings pages.
 *
 * Mirrors the semantics of the backend's `parse_score_text`
 * (backend/app/modules/matches/score_utils.py): tennis/padel heuristics
 * (super tiebreak collapses to 1-0 for a 3rd-or-later set with a value
 * >= 10) only apply when the sport uses SETS_Y_GAMES scoring. Pickleball
 * uses GAMES_A_11 — every parsed "game" (an 11-point game) is kept as-is,
 * since there is no super tiebreak in pickleball.
 */

import type { SportKey } from "./api";

/** Sports that use the tennis/padel SETS_Y_GAMES super-tiebreak heuristic. */
function usesTennisHeuristics(sport?: SportKey | string | null): boolean {
  return (sport ?? "tenis") !== "pickleball";
}

/**
 * True when the tournament/match sport is pickleball. Capture screens use
 * this to switch scoreboard vocabulary ("Game N" instead of "Set N"/"TB")
 * and to skip the tennis super-muerte "TB " prefix, which does not exist
 * in pickleball (every game, including the 3rd, is a normal game to 11).
 */
export function isPickleballSport(sport?: SportKey | string | null): boolean {
  return sport === "pickleball";
}

export function parseGames(
  scoreText: string | null | undefined,
  sport?: SportKey | string | null,
): Array<[number, number]> {
  if (!scoreText) return [];
  const tennisHeuristics = usesTennisHeuristics(sport);
  const out: Array<[number, number]> = [];
  const re = /(\d+)\s*[-–:/]\s*(\d+)/g;
  let match: RegExpExecArray | null;
  let setIndex = 0;
  while ((match = re.exec(scoreText)) !== null) {
    const a = Number(match[1]);
    const b = Number(match[2]);
    // Super muerte / match tiebreak (tenis/pádel only): score is often
    // stored as third token like 10-8 or 12-10. For DIF it counts as one
    // game won/lost, not ten games vs eight. Pickleball has no super
    // muerte — every game (incl. the 3rd) is a normal game to 11.
    if (tennisHeuristics && setIndex >= 2 && Math.max(a, b) >= 10) {
      out.push(a > b ? [1, 0] : [0, 1]);
    } else {
      out.push([a, b]);
    }
    setIndex += 1;
  }
  return out;
}
